/**
 * Sales records (/daily-sales, alias /sales/records): the ledger of sales rows. Analysis lives on the
 * Sales overview / performance pages; this page is for finding, checking, adding and exporting rows.
 *
 * - One summary line ("N records · ₹X · Y units in <period>") and the platforms with sales as chips
 *   (the platform filter). Search matches SKU, product, brand or a marketplace order ID.
 * - "Order / source" on each row: the order ID, or where the row came from (API, file, manual); click
 *   it for the row's detail (order, returns on that order, original marketplace code, who added it).
 * - Uploads: every uploaded file with Undo (admins and managers). One upload flow (SalesUploadDialog);
 *   "Add record" adds one row and hides platforms whose sales come from a marketplace API on that date.
 * - URL params date_from/date_to/date/platform/brand/category/sku/search pre-filter the table, so
 *   drill-downs (a day on the overview, "View records" in the product drawer) land filtered; date=
 *   becomes a one-day range. The page writes its filters back to the URL (replace).
 */
import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { getAuthHeaders, useAuth } from '../contexts/AuthContext';
import { useCountry } from '../contexts/CountryContext';
import { canEditModule, viewOnlyHint } from '../lib/permissions';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { Plus, FileSpreadsheet, Percent, RefreshCw, X, Calendar, Search, ChevronsUpDown, Check } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { platformLabel } from '../config/platforms';
import { LoadError, errorMessage } from '../components/LoadError';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { formatDateRange, indiaRangeToToday, indiaToday, isIsoDay, number, salesDateRange, shortMoney } from '../components/sales/format';
import { DailySalesTable, SalesPager } from '../components/sales/DailySalesTable';
import { SalesUploadDialog } from '../components/sales/SalesUploadDialog';
import { SalesUploadsDialog } from '../components/sales/SalesUploadsDialog';
import { SalesRowDrawer } from '../components/sales/SalesRowDrawer';
import { importMessage } from '../components/sales/overview/SalesPageHeader';
import { queryString, useAbortableGet, useDebouncedValue } from '../components/sales/useAbortableGet';
import { Skeleton } from '../components/ui/skeleton';

import { useChannels } from '../hooks/useChannels';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DEFAULT_PRESET = '30d';

// Date range presets, in India calendar days (IST). Rolling windows (7/30/90 days) are complete
// days ending yesterday, matching the Sales overview, so its drill-downs land on the same preset.
// "Today", Month to date, Year to date and All time include today so freshly added rows can be found.
const getDatePreset = (key) => {
  const todayStr = indiaToday();
  const toToday = (period) => { const range = indiaRangeToToday(period); return { from: range.date_from, to: range.date_to }; };
  const complete = (period) => { const range = salesDateRange(period); return { from: range.date_from, to: range.date_to }; };
  switch (key) {
    case 'today': return { from: todayStr, to: todayStr };
    case 'yesterday': return complete('yesterday');
    case '7d': return complete('7');
    case '30d': return complete('30');
    case '90d': return complete('90');
    case 'mtd': return toToday('mtd');
    case 'ytd': return toToday('ytd');
    case 'all': return { from: '2020-01-01', to: todayStr };
    default: return null;
  }
};

const DATE_PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: '7d', label: 'Last 7 days', hint: 'Yesterday and the 6 days before' },
  { key: '30d', label: 'Last 30 days', hint: 'Yesterday and the 29 days before' },
  { key: '90d', label: 'Last 90 days', hint: 'Yesterday and the 89 days before' },
  { key: 'mtd', label: 'Month to date', hint: 'From the 1st of this month to today' },
  { key: 'ytd', label: 'Year to date', hint: 'From 1 January to today' },
  { key: 'all', label: 'All time' },
  { key: 'custom', label: 'Custom' },
];
const PRESET_KEYS = DATE_PRESETS.map(p => p.key);

// Best preset for a bare date range (links that carry only dates). Some presets can cover the same
// dates on certain days (on the 1st, Month to date equals Today), so the default wins a tie and the
// page otherwise remembers the preset the user picked (state + the URL's `preset` param) rather than
// re-matching dates.
const matchPreset = (from, to) => [DEFAULT_PRESET, ...PRESET_KEYS.filter(k => k !== DEFAULT_PRESET)].find((key) => {
  const preset = getDatePreset(key);
  return preset && preset.from === from && preset.to === to;
}) || 'custom';

const QUICK_FILTER_LABELS = { high_discount: 'High discount' };
const URL_KEYS = ['preset', 'date_from', 'date_to', 'date', 'platform', 'brand', 'category', 'sku', 'search'];

const defaultFilters = () => {
  const preset = getDatePreset(DEFAULT_PRESET);
  return { preset: DEFAULT_PRESET, from: preset.from, to: preset.to, platform: 'ALL', brand: 'ALL', category: 'ALL', sku: '', search: '' };
};

// Filters described by the URL (missing keys fall back to defaults). Invalid dates are ignored.
const filtersFromUrl = (params) => {
  const base = defaultFilters();
  const get = key => (params.get(key) || '').trim();
  const from = get('date_from');
  const to = get('date_to');
  const day = get('date');
  const presetKey = get('preset');
  const named = presetKey !== 'custom' && PRESET_KEYS.includes(presetKey) ? getDatePreset(presetKey) : null;
  const hasRange = isIsoDay(from) && isIsoDay(to) && from <= to;
  if (isIsoDay(day)) {
    // A single day (older links, the overview's day drill-down) is a one-day range.
    base.from = day; base.to = day; base.preset = matchPreset(day, day);
  } else if (named && (!hasRange || (named.from === from && named.to === to))) {
    // A named preset is relative: it resolves against today's India date on every load.
    base.from = named.from; base.to = named.to; base.preset = presetKey;
  } else {
    if (hasRange) {
      base.from = from; base.to = to;
    } else if (isIsoDay(from) && !to) {
      base.from = from; base.to = from > indiaToday() ? from : indiaToday();
    }
    base.preset = matchPreset(base.from, base.to);
  }
  const platform = get('platform').toUpperCase();
  if (platform && platform !== 'ALL') base.platform = platform;
  if (get('brand') && get('brand') !== 'ALL') base.brand = get('brand');
  if (get('category') && get('category') !== 'ALL') base.category = get('category');
  // sku= (from drill-down links) is an exact SKU filter; search= stays free text.
  base.sku = get('sku').trim().slice(0, 80);
  base.search = get('search').slice(0, 120);
  return base;
};

const hasUrlFilters = params => URL_KEYS.some(key => params.get(key));

const filtersToUrl = (f) => {
  const params = new URLSearchParams();
  // Written from the preset the user chose (never re-matched from dates): named presets are
  // stored by key so a reload keeps them; custom ranges are stored as dates.
  if (f.preset === 'custom' || !PRESET_KEYS.includes(f.preset)) { params.set('date_from', f.from); params.set('date_to', f.to); }
  else if (f.preset !== DEFAULT_PRESET) params.set('preset', f.preset);
  if (f.platform !== 'ALL') params.set('platform', f.platform);
  if (f.brand !== 'ALL') params.set('brand', f.brand);
  if (f.category !== 'ALL') params.set('category', f.category);
  if (f.sku) params.set('sku', f.sku);
  if (f.search) params.set('search', f.search);
  return params;
};

const formatRupees = (value) => `₹${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

// Searchable SKU combobox for the "Add record" form (shows product name and ₹ price per option)
const SalesSkuCombobox = ({ id, value, skus, onPick, invalid, describedBy }) => {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const selected = useMemo(() => skus.find((s) => s.sku_code === value), [skus, value]);
  const results = useMemo(() => {
    const q = term.trim().toLowerCase();
    return skus.filter((s) => !q || `${s.sku_code} ${s.product_name || ''}`.toLowerCase().includes(q)).slice(0, 50);
  }, [skus, term]);
  return (
    <Popover open={open} onOpenChange={(next) => { setOpen(next); if (!next) setTerm(''); }}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          className={`h-9 w-full justify-between text-[13px] font-normal ${invalid ? 'border-red-500' : ''}`}
          data-testid="sku-select"
        >
          <span className={`truncate ${selected ? 'text-gray-900' : 'text-gray-500'}`}>
            {selected ? `${selected.sku_code} - ${selected.product_name || ''} (${formatRupees(selected.selling_price)})` : 'Select SKU'}
          </span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[min(320px,calc(100vw-2rem))] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput value={term} onValueChange={setTerm} placeholder="Search SKU code or product…" data-testid="sku-search-input" />
          <CommandList className="max-h-64">
            <CommandEmpty>No SKU found.</CommandEmpty>
            <CommandGroup>
              {results.map((s) => (
                <CommandItem
                  key={s.sku_code}
                  value={s.sku_code}
                  onSelect={() => { onPick(s.sku_code); setOpen(false); setTerm(''); }}
                  data-testid={`sku-option-${s.sku_code}`}
                >
                  <Check className={`mr-2 h-3.5 w-3.5 shrink-0 ${s.sku_code === value ? 'opacity-100' : 'opacity-0'}`} aria-hidden="true" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-xs">{s.sku_code}</span>
                      <span className="text-[11px] font-medium text-gray-700">{formatRupees(s.selling_price)}</span>
                    </div>
                    <div className="truncate text-[11px] text-gray-500">{s.product_name}</div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const emptyForm = () => ({ date: indiaToday(), sku_code: '', platform: '', units_sold: 1, deal_price: '' });

export const DailySalesPage = () => {
  const channelMaster = useChannels();   // also re-renders when channel names or colours change
  const { user } = useAuth();
  const canEditSales = canEditModule(user, 'sales');
  const { country, currency } = useCountry();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlString = searchParams.toString();
  const [skus, setSkus] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [savingSale, setSavingSale] = useState(false);
  const [skuError, setSkuError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [platformError, setPlatformError] = useState('');
  const [cutovers, setCutovers] = useState({});
  const [openRow, setOpenRow] = useState(null);
  const detailOpener = useRef(null);   // the button that opened a row's detail gets focus back when it closes
  const openDetail = sale => { detailOpener.current = document.activeElement; setOpenRow(sale); };

  // Filters (applied on the server). Initial values come from the URL so drill-downs land filtered.
  const initial = useMemo(() => filtersFromUrl(searchParams), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [selectedPlatform, setSelectedPlatform] = useState(initial.platform);
  const [selectedBrand, setSelectedBrand] = useState(initial.brand);
  const [selectedCategory, setSelectedCategory] = useState(initial.category);
  const [searchText, setSearchText] = useState(initial.search);
  const [skuFilter, setSkuFilter] = useState(initial.sku || '');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [quickFilter, setQuickFilter] = useState('none'); // 'none' or 'high_discount'
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [refreshKey, setRefreshKey] = useState(0);
  const search = useDebouncedValue(searchText.trim(), 350);

  // Date range (applied range drives the queries; custom inputs can be edited before Apply)
  const [datePreset, setDatePreset] = useState(initial.preset);
  const [dateFrom, setDateFrom] = useState(initial.from);
  const [dateTo, setDateTo] = useState(initial.to);
  const [appliedRange, setAppliedRange] = useState({ from: initial.from, to: initial.to, preset: initial.preset });
  const [showDatePicker, setShowDatePicker] = useState(initial.preset === 'custom');

  const fetchData = useCallback(() => setRefreshKey(k => k + 1), []);
  const withReset = setter => value => { setter(value); setPage(1); };

  const applyFilters = useCallback((f) => {
    setDatePreset(f.preset); setDateFrom(f.from); setDateTo(f.to); setAppliedRange({ from: f.from, to: f.to, preset: f.preset });
    setShowDatePicker(f.preset === 'custom');
    setSelectedPlatform(f.platform); setSelectedBrand(f.brand); setSelectedCategory(f.category);
    setSearchText(f.search); setSkuFilter(f.sku || ''); setQuickFilter('none'); setPage(1);
  }, []);

  // URL -> state when the URL changes from outside (a drill-down link, back/forward, the sidebar).
  const writtenUrl = useRef(null);
  useEffect(() => {
    if (urlString === writtenUrl.current) return;
    const params = new URLSearchParams(urlString);
    applyFilters(hasUrlFilters(params) ? filtersFromUrl(params) : defaultFilters());
  }, [urlString, applyFilters]);

  // State -> URL (replace, so filter tweaks do not flood history).
  const trimmedSearch = searchText.trim();
  useEffect(() => {
    const next = filtersToUrl({ preset: appliedRange.preset,
      from: appliedRange.from, to: appliedRange.to, platform: selectedPlatform, brand: selectedBrand, category: selectedCategory, sku: skuFilter, search: trimmedSearch }).toString();
    writtenUrl.current = next;
    if (next !== urlString) setSearchParams(new URLSearchParams(next), { replace: true });
    // urlString is deliberately left out: this effect only reacts to filter changes.
  }, [appliedRange, selectedPlatform, selectedBrand, selectedCategory, skuFilter, trimmedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  const scope = { date_from: appliedRange.from, date_to: appliedRange.to, brand: selectedBrand, category: selectedCategory, sku: skuFilter, search, country };
  const summaryState = useAbortableGet(`${API}/sales/summary?${queryString(scope)}`, refreshKey);
  const recordsQuery = queryString({ ...scope, platform: selectedPlatform });
  const recordsState = useAbortableGet(`${API}/sales/records?${recordsQuery}&${queryString({ sort_by: sortBy, sort_order: sortOrder, quick: quickFilter, page, page_size: pageSize })}`, refreshKey);
  const summary = summaryState.data;
  const records = recordsState.data || { rows: [], total: 0 };
  const loading = recordsState.loading;

  useEffect(() => {
    // Ignore a slower response for a country the user has already switched away from.
    const controller = new AbortController();
    axios.get(`${API}/skus?country=${country}`, { headers: getAuthHeaders(), signal: controller.signal })
      .then(res => { if (!controller.signal.aborted) setSkus(res.data); })
      .catch(() => { if (!controller.signal.aborted) toast.error('Failed to load SKUs'); });
    return () => controller.abort();
  }, [country]);

  useEffect(() => {
    // Platforms whose sales come from a marketplace API from a date on (Add record hides them for those dates)
    const controller = new AbortController();
    axios.get(`${API}/sales/cutovers?country=${country}`, { signal: controller.signal })
      .then(res => { if (!controller.signal.aborted) setCutovers(res.data?.cutovers || {}); })
      .catch(() => { if (!controller.signal.aborted) setCutovers({}); });
    return () => controller.abort();
  }, [country]);
  const apiOwned = (platform, day) => Boolean(cutovers[platform] && day && day >= cutovers[platform]);
  const saleChannels = channelMaster.filters
    .filter(c => c.status !== 'closed' || (c.closed_on && formData.date && formData.date <= c.closed_on))
    .map(c => ({ id: c.code, name: c.name }));
  const formPlatforms = saleChannels.filter(p => !apiOwned(p.id, formData.date));
  const hiddenPlatforms = saleChannels.filter(p => apiOwned(p.id, formData.date));

  const brandOptions = useMemo(() => {
    const set = new Set(skus.map(s => s.brand || 'Unknown'));
    if (selectedBrand !== 'ALL') set.add(selectedBrand);
    return [...set].sort();
  }, [skus, selectedBrand]);
  const categoryOptions = useMemo(() => {
    const set = new Set(skus.map(s => s.category || 'Unknown'));
    if (selectedCategory !== 'ALL') set.add(selectedCategory);
    return [...set].sort();
  }, [skus, selectedCategory]);

  const applyDatePreset = (key) => {
    setDatePreset(key);
    if (key === 'custom') {
      setShowDatePicker(true);
      return;
    }
    setShowDatePicker(false);
    const preset = getDatePreset(key);
    if (preset) {
      setDateFrom(preset.from);
      setDateTo(preset.to);
      setAppliedRange({ from: preset.from, to: preset.to, preset: key });
      setPage(1);
    }
  };

  const applyCustomRange = () => {
    if (!isIsoDay(dateFrom) || !isIsoDay(dateTo) || dateFrom > dateTo) {
      toast.error('Choose a start date on or before the end date');
      return;
    }
    // Typed dates that happen to equal a preset light that preset up (default wins a tie).
    const key = matchPreset(dateFrom, dateTo);
    setDatePreset(key);
    setShowDatePicker(key === 'custom');
    setAppliedRange({ from: dateFrom, to: dateTo, preset: key });
    setPage(1);
  };

  // One summary line for the platform selection; the chips list every platform with sales (the platform filter).
  const totals = useMemo(() => {
    const byPlatform = {};
    let records = 0; let revenue = 0; let units = 0;
    (summary?.by_date_platform || []).forEach(row => {
      const entry = byPlatform[row.platform] || (byPlatform[row.platform] = { platform: row.platform, revenue: 0, units: 0, records: 0 });
      entry.revenue += row.revenue || 0; entry.units += row.units || 0; entry.records += row.records || 0;
      if (selectedPlatform !== 'ALL' && row.platform !== selectedPlatform) return;
      records += row.records || 0; revenue += row.revenue || 0; units += row.units || 0;
    });
    const platforms = Object.values(byPlatform).filter(p => p.revenue > 0 || p.units > 0 || p.platform === selectedPlatform)
      .sort((a, b) => b.revenue - a.revenue);
    if (selectedPlatform !== 'ALL' && !platforms.some(p => p.platform === selectedPlatform)) {
      platforms.push({ platform: selectedPlatform, revenue: 0, units: 0, records: 0 });
    }
    return { records, revenue, units, platforms };
  }, [summary, selectedPlatform]);

  const thisYear = indiaToday().slice(0, 4);
  const periodText = formatDateRange(appliedRange.from, appliedRange.to, appliedRange.from.slice(0, 4) !== thisYear || appliedRange.to.slice(0, 4) !== thisYear);
  const money = value => (currency === '₹' ? shortMoney(value) : `${currency} ${Math.round(value).toLocaleString('en-IN')}`);
  const exactMoney = value => `${currency}${Math.round(value || 0).toLocaleString('en-IN')}`;

  const onSort = field => {
    setSortOrder(sortBy === field && sortOrder === 'desc' ? 'asc' : 'desc');
    setSortBy(field);
    setPage(1);
  };

  const onFilter = (key, value) => {
    if (key === 'platform') setSelectedPlatform(value);
    if (key === 'search') setSearchText(value);
    if (key === 'sku') setSkuFilter(value);
    setPage(1);
  };

  const hasFilters = selectedPlatform !== 'ALL' || selectedBrand !== 'ALL' || selectedCategory !== 'ALL' || !!skuFilter || !!searchText || quickFilter !== 'none' || sortBy !== 'date';

  const resetFilters = () => {
    setSelectedPlatform('ALL');
    setSelectedBrand('ALL');
    setSelectedCategory('ALL');
    setSkuFilter('');
    setSearchText('');
    setSortBy('date');
    setSortOrder('desc');
    setQuickFilter('none');
    setPage(1);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      // Same filters, quick filter and sort as the table, so the file matches the count shown.
      const response = await axios.get(`${API}/sales/export?${recordsQuery}&${queryString({ sort_by: sortBy, sort_order: sortOrder, quick: quickFilter })}`, { headers: getAuthHeaders(), responseType: 'blob' });
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sales_${appliedRange.from}_to_${appliedRange.to}${quickFilter === 'none' ? '' : `_${quickFilter}`}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      const exportedRows = Number(response.headers?.['x-row-count']);
      toast.success(Number.isFinite(exportedRows) && response.headers?.['x-row-count'] != null
        ? `Exported ${exportedRows.toLocaleString('en-IN')} sales rows${quickFilter === 'none' ? '' : ` (${QUICK_FILTER_LABELS[quickFilter]})`}`
        : 'Sales export downloaded');
    } catch {
      toast.error('Failed to export sales data');
    } finally {
      setExporting(false);
    }
  };

  // After an upload, show the imported dates (capped to a year) so the new rows are visible.
  const handleImported = (result) => {
    toast.success(importMessage(result));
    if (isIsoDay(result.date_from) && isIsoDay(result.date_to) && !result.already_imported) {
      const span = (Date.parse(result.date_to) - Date.parse(result.date_from)) / 864e5;
      const from = span < 366 ? result.date_from : result.date_to.slice(0, 4) + '-01-01';
      const key = matchPreset(from, result.date_to);
      setDatePreset(key); setShowDatePicker(key === 'custom');
      setDateFrom(from); setDateTo(result.date_to);
      setAppliedRange({ from, to: result.date_to, preset: key });
      setPage(1);
    }
    fetchData();
  };

  const getSelectedSkuPrice = () => {
    const sku = skus.find(s => s.sku_code === formData.sku_code);
    return sku?.selling_price || 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (savingSale) return;
    if (!formData.platform || apiOwned(formData.platform, formData.date)) {
      setPlatformError('Choose a platform');
      return;
    }
    setPlatformError('');
    if (!formData.sku_code) {
      setSkuError('Select a SKU');
      return;
    }
    setSkuError('');
    const payload = {
      date: formData.date,
      sku_code: formData.sku_code,
      platform: formData.platform,
      units_sold: formData.units_sold,
      deal_price: formData.deal_price !== '' ? parseFloat(formData.deal_price) : null,
      country
    };
    setSavingSale(true);
    try {
      await axios.post(`${API}/sales`, payload, { headers: getAuthHeaders() });
      const added = payload.date;
      const outside = added < appliedRange.from || added > appliedRange.to;
      toast.success('Sales record added', outside ? {
        description: `${formatDateRange(added, added, true)} is outside the dates shown.`,
        action: { label: 'Show that day', onClick: () => applyFilters({ ...defaultFilters(), preset: matchPreset(added, added), from: added, to: added }) },
      } : undefined);
      setDialogOpen(false);
      setFormData(emptyForm());
      fetchData();
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to add sales record'));
    } finally {
      setSavingSale(false);
    }
  };

  const summaryLoading = summaryState.loading && !summary;

  return (
    <div className="space-y-5" data-testid="daily-sales-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl" data-testid="daily-sales-heading">Sales records</h1>
          <p className="mt-1 text-[13px] text-gray-500">Every sales row by day, SKU and platform. Upload files, add a record or export.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={handleExport} disabled={exporting} variant="outline" className="h-9 text-[13px]" title="Exports the rows matching the current filters, quick filter and sort" data-testid="export-sales-button">
            <FileSpreadsheet className="h-4 w-4 mr-2" aria-hidden="true" />
            {exporting ? 'Exporting…' : 'Export'}
          </Button>
          {!canEditSales && (
            <span className="text-xs text-gray-600" data-testid="daily-sales-view-only">View only · {viewOnlyHint(user)}</span>
          )}
          {canEditSales && (
            <Dialog open={dialogOpen} onOpenChange={(open) => { if (savingSale) return; setDialogOpen(open); if (!open) { setSkuError(''); setPlatformError(''); } }}>
              <DialogTrigger asChild>
                <Button variant="outline" className="h-9 px-3 text-[13px]" data-testid="add-sales-button">
                  <Plus className="h-4 w-4 mr-2" aria-hidden="true" /> Add record
                </Button>
              </DialogTrigger>
              <DialogContent className="w-[calc(100vw-2rem)] max-w-lg">
                <DialogHeader>
                  <DialogTitle className="text-[17px] font-semibold">Add sales record</DialogTitle>
                  <DialogDescription>
                    Add one platform sales record for a SKU and date.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="sales-form-date" className="text-[13px]">Date *</Label>
                      <Input id="sales-form-date" type="date" value={formData.date} max={indiaToday()}
                        onChange={(e) => {
                          const date = e.target.value;
                          setFormData({ ...formData, date, platform: apiOwned(formData.platform, date) ? '' : formData.platform });
                        }} required className="h-9 text-[13px]" data-testid="date-input" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="sales-form-platform" className="text-[13px]">Platform *</Label>
                      <Select value={formData.platform} onValueChange={(val) => { setFormData({ ...formData, platform: val }); setPlatformError(''); }}>
                        <SelectTrigger id="sales-form-platform" className={`h-9 ${platformError ? 'border-red-500' : ''}`} aria-invalid={platformError ? true : undefined}
                          aria-describedby={platformError ? 'sales-form-platform-error' : undefined} data-testid="platform-select">
                          <SelectValue placeholder="Choose platform" />
                        </SelectTrigger>
                        <SelectContent>
                          {formPlatforms.map(p => (
                            <SelectItem key={p.id} value={p.id}>{platformLabel(p.id)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {platformError && <p id="sales-form-platform-error" role="alert" className="text-[12px] text-red-600">{platformError}</p>}
                      {hiddenPlatforms.length > 0 && (
                        <p className="text-[11px] text-gray-500" data-testid="platform-api-note">
                          {hiddenPlatforms.map(p => platformLabel(p.id)).join(', ')} {hiddenPlatforms.length === 1 ? 'comes' : 'come'} from the marketplace API on this date.
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="sales-form-sku" className="text-[13px]">SKU *</Label>
                    <SalesSkuCombobox
                      id="sales-form-sku"
                      value={formData.sku_code}
                      skus={skus}
                      invalid={!!skuError}
                      describedBy={skuError ? 'sales-form-sku-error' : undefined}
                      onPick={(val) => { setFormData({ ...formData, sku_code: val }); setSkuError(''); }}
                    />
                    {skuError && (
                      <p id="sales-form-sku-error" role="alert" className="text-[12px] text-red-600" data-testid="sku-error">{skuError}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="sales-form-units" className="text-[13px]">Units sold *</Label>
                    <Input id="sales-form-units" type="number" value={formData.units_sold}
                      onChange={(e) => setFormData({ ...formData, units_sold: parseInt(e.target.value, 10) || 0 })}
                      required min="1" className="h-9 text-[13px]" data-testid="units-sold-input" />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="sales-form-deal-price" className="text-[13px] flex flex-wrap items-center gap-2">
                      Selling price per unit ({currency})
                      <span className="text-[11px] text-gray-500 font-normal">Leave empty to use the list price</span>
                    </Label>
                    <div className="relative">
                      <Input id="sales-form-deal-price" type="number" step="0.01" min="0"
                        placeholder={formData.sku_code ? `List: ${currency}${getSelectedSkuPrice()}` : 'Select SKU first'}
                        value={formData.deal_price}
                        onChange={(e) => setFormData({ ...formData, deal_price: e.target.value })}
                        className="h-9 text-[13px]" data-testid="deal-price-input" />
                      {formData.deal_price && formData.sku_code && getSelectedSkuPrice() > 0 && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 text-[11px]">
                          {parseFloat(formData.deal_price) < getSelectedSkuPrice() ? (
                            <span className="text-red-600 flex items-center gap-0.5">
                              <Percent className="h-3 w-3" aria-hidden="true" />
                              {Math.round((1 - parseFloat(formData.deal_price) / getSelectedSkuPrice()) * 100)}% off
                            </span>
                          ) : (
                            <span className="text-green-700">No discount</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 pt-4 border-t">
                    <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); setSkuError(''); setPlatformError(''); }} disabled={savingSale} className="h-9 text-[13px]">Cancel</Button>
                    <Button type="submit" disabled={savingSale} className="h-9 bg-[#007AFF] hover:bg-[#0066CC] text-[13px]" data-testid="save-sales-button">{savingSale ? 'Saving…' : 'Save'}</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
          <SalesUploadsDialog country={country} canUndo={canEditSales} onChanged={() => { setPage(1); fetchData(); }} />
          {canEditSales && <SalesUploadDialog onImported={handleImported} />}
        </div>
      </div>

      {/* Date range + one-line summary */}
      <Card className="p-4 bg-white rounded-2xl border-0 shadow-sm space-y-3" data-testid="date-range-picker">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-[12px] text-gray-500">
            <Calendar className="h-4 w-4" aria-hidden="true" />
            <span className="font-medium" id="sales-date-range-label">Date range</span>
          </div>
          <div className="flex items-center bg-gray-50 rounded-xl p-1 gap-0.5 flex-wrap" role="group" aria-labelledby="sales-date-range-label" data-testid="date-presets">
            {DATE_PRESETS.map(({ key, label, hint }) => (
              <button
                key={key}
                type="button"
                title={hint}
                onClick={() => applyDatePreset(key)}
                aria-pressed={datePreset === key}
                className={`px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all whitespace-nowrap ${
                  datePreset === key ? 'bg-gray-900 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900 hover:bg-white'
                }`}
                data-testid={`date-preset-${key}`}
              >
                {label}
              </button>
            ))}
          </div>
          {(datePreset === 'custom' || showDatePicker) && (
            <div className="flex flex-wrap items-center gap-2">
              <Input type="date" aria-label="Start date" value={dateFrom} max={indiaToday()} onChange={e => setDateFrom(e.target.value)} className="h-8 text-[12px] w-[140px] rounded-lg border-gray-200" data-testid="date-from-input" />
              <span className="text-[11px] text-gray-500">to</span>
              <Input type="date" aria-label="End date" value={dateTo} max={indiaToday()} onChange={e => setDateTo(e.target.value)} className="h-8 text-[12px] w-[140px] rounded-lg border-gray-200" data-testid="date-to-input" />
              <Button onClick={applyCustomRange} size="sm" className="h-8 px-3 bg-gray-900 text-white text-[11px] rounded-lg" data-testid="apply-custom-date">Apply</Button>
            </div>
          )}
          <Button variant="ghost" size="icon" className="ml-auto h-8 w-8" aria-label="Refresh sales records" onClick={fetchData} disabled={loading} data-testid="sales-records-refresh">
            <RefreshCw className={`h-4 w-4 ${loading || summaryState.loading ? 'animate-spin' : ''}`} aria-hidden="true" />
          </Button>
        </div>

        {summaryState.error && !summary ? (
          <LoadError what="the period totals" error={summaryState.error} onRetry={fetchData} retrying={summaryState.loading} testId="sales-summary-error" />
        ) : (
          <div className={`flex flex-wrap items-baseline gap-x-3 gap-y-1 text-[13px] transition-opacity ${(summaryState.loading || summaryState.error) && summary ? 'opacity-60' : ''}`} aria-live="polite">
            {summaryState.error && summary && (
              <p role="alert" className="flex w-full flex-wrap items-center gap-x-2 text-[12px] text-amber-800" data-testid="sales-summary-stale">
                Couldn't update the totals, so the figures below may be from your previous selection.
                <button type="button" onClick={fetchData} disabled={summaryState.loading}
                  className="font-medium text-blue-700 underline underline-offset-2 hover:text-blue-900 disabled:opacity-50" data-testid="sales-summary-retry">
                  {summaryState.loading ? 'Retrying…' : 'Retry'}
                </button>
              </p>
            )}
            {summaryLoading ? <Skeleton className="h-5 w-72" /> : (
              <p className="text-gray-900" data-testid="sales-range-record-count">
                <span className="font-semibold tabular-nums">{number(totals.records)}</span> records ·{' '}
                <span className="font-semibold tabular-nums" title={exactMoney(totals.revenue)}>{money(totals.revenue)}</span> ·{' '}
                <span className="font-semibold tabular-nums">{number(totals.units)}</span> units in {periodText}
                {selectedPlatform !== 'ALL' && <span className="text-gray-500"> on {platformLabel(selectedPlatform)}</span>}
                {quickFilter !== 'none' && <span className="text-gray-500" data-testid="sales-summary-quick-note"> (all rows in the period, before the {QUICK_FILTER_LABELS[quickFilter]} quick filter)</span>}
              </p>
            )}
            {!summaryLoading && totals.platforms.length > 0 && (
              <ul className="flex w-full flex-wrap gap-1.5 text-[12px] text-gray-600" aria-label="Filter by platform" data-testid="sales-platform-breakdown">
                {[{ platform: 'ALL' }, ...totals.platforms].map(p => {
                  const active = selectedPlatform === p.platform;
                  const all = p.platform === 'ALL';
                  return (
                    <li key={p.platform}>
                      <button type="button" onClick={() => { setSelectedPlatform(p.platform); setPage(1); }} aria-pressed={active}
                        className={`rounded-full border px-2.5 py-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 ${active ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
                        title={all ? 'Show every platform' : `${platformLabel(p.platform)}: ${exactMoney(p.revenue)} · ${number(p.units)} units · ${number(p.records)} records`}
                        data-testid={`sales-platform-chip-${p.platform}`}>
                        {all ? 'All platforms' : <>{platformLabel(p.platform)} <span className={`font-medium tabular-nums ${active ? 'text-white' : 'text-gray-900'}`}>{money(p.revenue)}</span></>}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </Card>

      {/* Filter and Sort Controls */}
      <Card className="bg-white rounded-2xl border-0 shadow-sm p-4" data-testid="sales-filters">
        <div className="flex flex-wrap items-center gap-3">
          <Select value={selectedBrand} onValueChange={withReset(setSelectedBrand)}>
            <SelectTrigger className="w-[140px] h-9 text-[13px]" aria-label="Brand" data-testid="brand-filter"><SelectValue placeholder="Brand" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All brands</SelectItem>
              {brandOptions.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={selectedCategory} onValueChange={withReset(setSelectedCategory)}>
            <SelectTrigger className="w-[150px] h-9 text-[13px]" aria-label="Category" data-testid="category-filter"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All categories</SelectItem>
              {categoryOptions.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="relative w-full sm:w-auto">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" aria-hidden="true" />
            <Input value={searchText} onChange={e => { setSearchText(e.target.value); setPage(1); }} placeholder="SKU, product or order ID" aria-label="Search SKU, product or order ID" className="h-9 w-full sm:w-[240px] pl-8 pr-8 text-[13px]" data-testid="sales-search-input" />
            {searchText && (
              <button type="button" aria-label="Clear search" onClick={() => { setSearchText(''); setPage(1); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700">
                <X className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            )}
          </div>
          {skuFilter && (
            <span className="inline-flex h-9 items-center gap-1 rounded-full bg-blue-50 px-3 text-[13px] font-medium text-blue-800" data-testid="sales-sku-filter">
              SKU: {skuFilter}
              <button type="button" aria-label={`Remove SKU filter ${skuFilter}`} onClick={() => { setSkuFilter(''); setPage(1); }} className="ml-1 rounded-full p-0.5 hover:bg-blue-100">
                <X className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </span>
          )}

          <Button variant={quickFilter === 'high_discount' ? 'default' : 'outline'} size="sm" aria-pressed={quickFilter === 'high_discount'}
            title="Rows sold at 10% or more below the list price"
            className={`h-9 text-[12px] ${quickFilter === 'high_discount' ? 'bg-red-600 hover:bg-red-700' : ''}`}
            onClick={() => { setQuickFilter(quickFilter === 'high_discount' ? 'none' : 'high_discount'); setPage(1); }} data-testid="filter-high-discount">
            <Percent className="h-3 w-3 mr-1" aria-hidden="true" />High discount
          </Button>

          {hasFilters && (
            <Button variant="ghost" size="sm" className="h-8 text-[11px] text-gray-600" onClick={resetFilters} data-testid="reset-filters">
              <X className="h-3 w-3 mr-1" aria-hidden="true" />
              Reset
            </Button>
          )}

          <div className="ml-auto text-[12px] text-gray-500" data-testid="sales-results-count" aria-live="polite">
            {quickFilter === 'none'
              ? `${records.total.toLocaleString('en-IN')} matching records`
              : `${records.total.toLocaleString('en-IN')} records in ${QUICK_FILTER_LABELS[quickFilter]}`}
          </div>
        </div>
      </Card>

      <Card className="bg-white rounded-2xl border-0 shadow-sm overflow-hidden">
        {recordsState.error ? (
          <LoadError what="sales records" error={recordsState.error} onRetry={fetchData} retrying={loading} className="m-4" testId="sales-records-error" />
        ) : (
          <>
            <DailySalesTable rows={records.rows} loading={loading} currency={currency} sortBy={sortBy} sortOrder={sortOrder}
              onSort={onSort} hasFilters={hasFilters} onReset={resetFilters} onFilter={onFilter} onOpen={openDetail} canAdd={canEditSales} />
            <SalesPager page={page} pageSize={pageSize} total={records.total} loading={loading}
              onPage={setPage} onPageSize={size => { setPageSize(size); setPage(1); }} />
          </>
        )}
      </Card>
      <SalesRowDrawer sale={openRow} country={country} currency={currency} canEdit={canEditSales} onClose={() => setOpenRow(null)} onFilter={onFilter} returnFocus={detailOpener} />
    </div>
  );
};
